import type { ReactNode } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Rarity, Skin } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconSearch } from '@sorare/core/src/atoms/icons/system/iconSearch.ts';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import type { CurrentUser } from '@sorare/core/src/contexts/currentUser';
import {
  useFacet,
  selectOne,
  useGQLSearchContext,
} from '@sorare/core/src/contexts/graphql-search';
import { BackendSearchBox } from '@sorare/marketplace/src/search/SearchBox';
import { messages } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch/SearchLayout';

import { CollectionNavigationSwitch } from '#src/components/myCards/CollectionNavigationSwitch/index.tsx';
import { SkinFilter } from '#src/components/myCards/SkinFilter/index.tsx';

import { BackendToggleFiltersButton } from '../ToggleFiltersButton/index.tsx';

const Header = styled(Vertical)`
  padding-bottom: var(--double-unit);
  margin: 0 calc(var(--double-unit));
  border-bottom: 1px solid var(--c-nd-150);
`;

const Divider = styled.div`
  width: 1px;
  height: 32px;
  background: var(--c-nd-100);
`;

type Props = {
  customFilter?: ReactNode;
  user: CurrentUser;
  rarity?: Rarity;
  toggleFilters: () => void;
  cardSetSlug: Nullable<string>;
};

export const BackendMyCardsSearchHeader = ({
  customFilter,
  user,
  rarity,
  toggleFilters,
  cardSetSlug,
}: Props) => {
  const { formatMessage } = useIntl();
  const ctx = useGQLSearchContext();
  const { refine } = useFacet('skin');

  const minimizable = !!cardSetSlug;
  const [minimizeSearch, setMinimizeSearch] = useState(minimizable);

  const activeSkins = ctx?.activeFacets.skin ?? [];
  const activeSkin = activeSkins[0]
    ? (activeSkins[0].toUpperCase() as Skin)
    : undefined;

  const handleSkinChange = (skin: Skin | undefined) => {
    if (!skin) {
      refine(selectOne(activeSkins[0]));
      return;
    }
    refine(selectOne(skin.toLowerCase()));
  };

  const searchPlaceholder = rarity
    ? messages.rarityCardsPlaceholder
    : messages.defaultPlaceholder;

  return (
    <Portal id="page-header-second-row">
      <Header>
        {customFilter}
        <SBHorizontal>
          <CollectionNavigationSwitch
            user={user}
            rarity={rarity}
            cardSetSlug={cardSetSlug}
          />
          {minimizeSearch ? (
            <Horizontal gap={0.5}>
              {!!cardSetSlug && (
                <>
                  <Divider />
                  <SkinFilter
                    skin={activeSkin}
                    onSkinChange={handleSkinChange}
                  />
                  <Divider />
                </>
              )}
              <Badge color="grey" badgeContent={ctx?.query.trim() ? 1 : 0}>
                <IconButton
                  icon={iconSearch}
                  size="small"
                  color="quaternary"
                  onClick={() => setMinimizeSearch(false)}
                />
              </Badge>
              <BackendToggleFiltersButton toggleFilters={toggleFilters} />
            </Horizontal>
          ) : (
            <Horizontal
              gap={0.5}
              onBlur={e => {
                if (minimizable && !e.currentTarget.contains(e.relatedTarget)) {
                  setMinimizeSearch(true);
                }
              }}
            >
              <BackendSearchBox
                placeholder={formatMessage(searchPlaceholder)}
                autoFocus={minimizable}
              />
              <BackendToggleFiltersButton toggleFilters={toggleFilters} />
            </Horizontal>
          )}
        </SBHorizontal>
      </Header>
    </Portal>
  );
};
